import React, { useContext, useEffect, useState } from 'react'
import { RouteChildrenProps } from 'react-router'
import * as Sentry from '@sentry/browser'
import { CognitoUser } from 'amazon-cognito-identity-js'

import { Auth } from 'aws-amplify'

import './LoginPage.scss'
import { Login } from './Login'
import { ResetTemporaryPassword } from './ResetTemporaryPassword'
import { NotificationType } from '../../components/Notifications'
import { NotificationContext, RefreshAuthData } from '../../contexts'

interface LoginPageProps extends RouteChildrenProps<any, { newPasswordCodeVerified: boolean }> {
  refreshAuthData: RefreshAuthData
}

const makeErrorMessage = (message: string) => {
  if (
    message.includes('Bad password') ||
    message.includes('User not found') ||
    message.includes('User does not exist')
  ) {
    return 'Incorrect username or password'
  }
  if (message.includes('User not in a group')) return 'You do not have permission. Contact an admin for access'

  if (message.includes('Internal server error') || message.includes('Bad triggerSource')) {
    return 'There was an error. Please contact an admin'
  }

  return message.toString()
}

export const LoginPage: React.FunctionComponent<LoginPageProps> = ({ refreshAuthData, history, location }) => {
  const [changeTemporaryPasswordCognitoUser, setChangeTemporaryPasswordCognitoUser] = useState<CognitoUser | null>()
  const { setNotification } = useContext(NotificationContext)

  useEffect(() => {
    if (location.state && location.state.newPasswordCodeVerified) {
      setNotification({
        text: 'You have successfully created a new password',
        type: NotificationType.SUCCESS,
      })
      history.replace('/login', {})
    }
    return () => {
      setNotification(null)
    }
  }, [history, location, setNotification])

  const onLoginSuccess = (username: string) => {
    Sentry.configureScope(scope => {
      scope.setUser({ email: username })
    })

    Sentry.withScope(scope => {
      scope.setLevel(Sentry.Severity.Debug)
      scope.setTag('feature', 'login')
      scope.setTag('subfeature', 'onLoginSuccess')
      Sentry.captureMessage('Login successful')
    })
  }

  const onLoginFail = (err: Error, username: string) => {
    Sentry.withScope(scope => {
      scope.setLevel(Sentry.Severity.Debug)
      scope.setTag('feature', 'login')
      scope.setTag('subfeature', 'onLoginFail')
      scope.setTag('username', username)
      Sentry.captureMessage(`${err.name ? `${err.name} - ` : ''}${err.message}`)
    })

    if (err.name === 'PasswordResetRequiredException') {
      history.push('/verify-password')
      return
    }

    setNotification({ text: makeErrorMessage(err.message), type: NotificationType.ERROR })
  }

  const handleLogin = (username: string, password: string): Promise<CognitoUser | any> => {
    return Auth.signIn(username, password)
      .then(cognitoUser => {
        sessionStorage.setItem('expiration_date', String(new Date().getTime() + 1000 * 60 * 60 * 5))

        if (cognitoUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
          Sentry.withScope(scope => {
            scope.setLevel(Sentry.Severity.Debug)
            scope.setTag('feature', 'login')
            scope.setTag('subfeature', 'onRequestResetTemporaryPassword')
            scope.setTag('username', username)
            Sentry.captureMessage('New password required')
          })

          setChangeTemporaryPasswordCognitoUser(cognitoUser)
          return
        }
        refreshAuthData()
        onLoginSuccess(username)
      })
      .catch((err: Error) => {
        onLoginFail(err, username)
        throw err
      })
  }

  const handleResetTemporaryPassword = (password: string): Promise<CognitoUser | any> => {
    const user: CognitoUser = changeTemporaryPasswordCognitoUser!
    return Auth.completeNewPassword(user, password, {})
      .then(() => {
        refreshAuthData()
        onLoginSuccess(user.getUsername())
      })
      .catch((err: Error) => {
        onLoginFail(err, user.getUsername())
        return Promise.reject()
      })
  }

  return (
    <div className="row">
      <div className="col-md-offset-3 col-md-6">
        <div className="panel reference-box">
          {!changeTemporaryPasswordCognitoUser && <Login handleLogin={handleLogin} />}
          {changeTemporaryPasswordCognitoUser && (
            <ResetTemporaryPassword handleResetTemporaryPassword={handleResetTemporaryPassword} />
          )}
        </div>
      </div>
    </div>
  )
}
